// UNIVERSAL, IDEMPOTENT widget-initialization layer.
//
// Purpose: permanently kill the recurring "a JS widget doesn't initialize on dynamically-
// added or re-rendered content" class of bug. A form widget must be initialized exactly
// once, correctly, no matter HOW its markup arrived:
//   • the initial page render (turbo:load / DOMContentLoaded),
//   • a turbo-frame load (turbo:frame-load),
//   • a turbo-STREAM replacement (create/update/destroy.turbo_stream — fires NEITHER
//     turbo:load NOR turbo:frame-load, so nothing hooked on those ever ran on it),
//   • a jQuery `add_child` insert ("Add a trade-in / cobuyer / commission / …"),
//   • any other post-load injection (Vue lists, modals, AJAX partials).
//
// DIVISION OF LABOUR with Stimulus:
//   • Stimulus controllers (choices / flatpickr / tinymce) own TARGET-based widgets — the
//     lazy registry in controllers/index.js already registers them and Stimulus' own
//     observer fires *TargetConnected for elements added by add_child AND turbo-stream.
//     Those controllers share this layer's template guard (template_guard.js) so the
//     init-then-clone trap is closed for them in ONE place.
//   • THIS layer owns the NON-Stimulus widgets that had no such re-init path and so broke
//     on dynamic/streamed content: the app-wide flatpickr `.date-input`, the legacy jQuery
//     `select.combobox`, and the `.currency_decimal` display formatter. They are initialized
//     here on ALL of the arrival paths above, idempotently.
//
// Every initializer is IDEMPOTENT: it respects the existing per-element guards
// (`element._flatpickr`, our `_comboboxBound`, a "$" already in a currency value) so a
// second pass (e.g. turbo:load AND the MutationObserver both seeing the same node) never
// double-initializes or mangles an already-initialized element. And every initializer
// funnels through `isInertTemplate` so template markup is never touched.
import flatpickr from 'flatpickr'
import 'flatpickr/dist/flatpickr.min.css'
import { isInertTemplate } from '~/javascripts/template_guard'
import { onDomReady } from '~/javascripts/dom_ready'

// Resolve a mutation/target/root argument down to a scannable node.
function scopeNode(root) {
  if (!root) return document
  if (root.nodeType === Node.DOCUMENT_NODE || root.nodeType === Node.ELEMENT_NODE) return root
  return document
}

// Run `fn` for every element matching `selector` at or under `root` (so we also catch the
// case where `root` itself is the widget element — common for add_child single-node inserts).
function eachMatch(root, selector, fn) {
  const scope = scopeNode(root)
  if (scope.matches && scope.matches(selector)) fn(scope)
  if (scope.querySelectorAll) scope.querySelectorAll(selector).forEach(fn)
}

// ── flatpickr on the app-wide `.date-input` (NON-Stimulus) ────────────────────────────
// This is the widget that most visibly broke on streamed/dynamic content: the global hook
// only ran on turbo:load / turbo:frame-load, so a `.date-input` added by add_child or by a
// cost-form turbo_stream replace never got a calendar. Now it re-inits on every path.
function initDateInputs(root) {
  eachMatch(root, '.date-input', (el) => {
    if (isInertTemplate(el)) return
    // Double-init guard spans BOTH flatpickr paths: flatpickr sets `el._flatpickr`, and the
    // legacy hook set `data-fp-bound`. Honoring both means a field that is ALSO a Stimulus
    // `flatpickrDate` target (e.g. the v2 inventory-cost Date field) is never wrapped twice —
    // whichever path reaches it first wins, the other skips.
    if (el._flatpickr || el.dataset.fpBound) return
    el.dataset.fpBound = '1'
    // disableMobile: flatpickr's native-mobile overlay mis-syncs the m/d/Y value back to the
    // input (dropping month+day → 01/01/YYYY silent data loss on phones), so force flatpickr's
    // own calendar everywhere. allowInput keeps manual typing (and the slash auto-formatter).
    flatpickr(el, { dateFormat: 'm/d/Y', allowInput: true, disableMobile: true })
  })
}

// ── legacy jQuery `select.combobox` (NON-Stimulus) ────────────────────────────────────
function initComboboxes(root) {
  const jq = window.jQuery
  if (!jq || !jq.fn || !jq.fn.combobox) return
  eachMatch(root, 'select.combobox', (el) => {
    if (isInertTemplate(el)) return
    if (el._comboboxBound) return
    el._comboboxBound = true
    jq(el).combobox()
  })
}

// ── `.currency_decimal` display formatter (NON-Stimulus) ──────────────────────────────
// Format an unformatted money field to "$#,###.##" so a value rendered into a dynamically-
// added row (trade-in ACV, …) shows formatted immediately instead of only on blur/calculate.
// IDEMPOTENT: skip anything already carrying "$" (never double-format — the historic $X.X0
// doubling) and never touch a value the parser can't read. Data the pricing solver reads stays
// clean because only the DISPLAY string changes and submit-time `toNumber()` strips it again.
function initCurrencyFields(root) {
  eachMatch(root, 'input.currency_decimal', (el) => {
    if (isInertTemplate(el)) return
    const raw = el.value
    if (raw == null || raw === '' || String(raw).includes('$')) return
    const n = parseFloat(String(raw).replace(/[^0-9.\-]/g, ''))
    if (isNaN(n)) return
    el.value = '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  })
}

// ── `.number` display formatter (integer + thousands separator) ───────────────────────
// The v2 deal form only ran initCurrencyFields, so `.number` fields (e.g. vehicle Mileage)
// rendered raw ("201648") instead of grouped ("201,648"). Same idempotent contract as the
// currency formatter: skip blanks / already-grouped values, and submit-time `toNumber()`
// (deal.js) strips the commas again, so the value the backend parses is unchanged.
function initNumberFields(root) {
  eachMatch(root, 'input.number', (el) => {
    if (isInertTemplate(el)) return
    const raw = el.value
    if (raw == null || raw === '' || String(raw).includes(',')) return
    const n = parseFloat(String(raw).replace(/[^0-9.\-]/g, ''))
    if (isNaN(n)) return
    el.value = n.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  })
}

// Public entry point: initialize every non-Stimulus widget under `root`, idempotently.
export function initWidgets(root = document) {
  initDateInputs(root)
  initComboboxes(root)
  initCurrencyFields(root)
  // Safe to group `.number` fields (mileage): Inventory#mileage= now strips the grouping comma
  // server-side, so a formatted "94,385" always persists as 94385 no matter the submit path.
  initNumberFields(root)
}

// ── Wiring: run initWidgets on every arrival path ─────────────────────────────────────
// This module self-wires exactly once (ESM modules are singletons), regardless of how many
// files import it, so importing `{ isInertTemplate }`-adjacent helpers here is free.
let wired = false
function wire() {
  if (wired || typeof document === 'undefined') return
  wired = true

  // Initial render. The module runs after DOM parse (it's an ESM bundle), but keep
  // DOMContentLoaded too for safety on the legacy shell, plus turbo:load for Frame/visit.
  const runAll = () => initWidgets(document)
  onDomReady(runAll)
  document.addEventListener('turbo:load', runAll)
  document.addEventListener('turbo:frame-load', (e) => initWidgets(e.target || document))

  // add_child (deal.js / inventory.js) triggers this custom event with the inserted content,
  // giving us an immediate, precisely-scoped init the moment the row is cloned in.
  if (window.jQuery) {
    window.jQuery(document).on('motorlot:child-added', (_e, inserted) => {
      const node = inserted && inserted.get ? inserted.get(0) : inserted
      if (node) initWidgets(node)
    })
  }

  // Catch-all for EVERYTHING injected after load — turbo-stream replacements (which fire no
  // turbo:* event), add_child, Vue-rendered lists, AJAX partials, modals. This mirrors the
  // Stimulus registry's observer (controllers/index.js): Stimulus handles registered
  // controllers/targets, this handles the non-Stimulus widgets. Scoped to each added element
  // subtree, so work is proportional to what actually changed (never a full-document rescan).
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) initWidgets(node)
      })
    }
  }).observe(document.documentElement, { childList: true, subtree: true })
}

wire()
